// piano-frontend/src/pages/AdminLessonPageV2.js
//
// 레슨 시간표 v2 — "캘린더 칩" 디자인.
// 데이터/핸들러 로직은 AdminLessonPage(v1)와 동일하게 유지하고 표현 계층만 재설계했다.
// v1을 그대로 남겨두기 위해 의도적으로 별도 파일로 분리(로직 중복 허용).
//
// 디자인 원칙
//  - 선생님 = 색(TEACHER_PALETTE, 운영 설정 탭과 공유). 예약 칩은 옅은 틴트 면 + 왼쪽 3px 컬러 바.
//  - 빈자리는 "그려진 것"이 아니라 점선 고스트. 예외(예약/블록/미출근)만 눈에 띄게.
//  - PC(md↑)는 폭을 넓혀 셀 안에 회원명 + 선생님명 + 상태 배지까지, 모바일은 회원명만.
//  - 색 외에 텍스처(빗금)/테두리로도 상태를 구분해 단일 색 채널 의존을 피한다.

import React, { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Button, DialogTitle, DialogContent, DialogActions,
  Stack, Chip, TextField, FormControlLabel, Checkbox, ButtonBase, Tooltip,
  FormControl, IconButton, Select, MenuItem, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert, Divider,
  CircularProgress, LinearProgress, useTheme, useMediaQuery
} from "@mui/material";
import Dialog from "../components/AppDialog";
import {
  Settings, LockOpen, Delete, CalendarToday, Edit, Check,
  ArrowBack, EventBusy, EventAvailable
} from "@mui/icons-material";
import api from "../api/axios";
import dayjs from "dayjs";
import LessonConfigDialog from "../components/LessonConfigDialog";
import DateImpactPreview from "../components/DateImpactPreview";
import MemberPicker from "../components/MemberPicker";
import DateField from "../components/DateField";
import WeekJumpPicker, { getCurrentWeekStart } from "../components/WeekJumpPicker";
import useWeekTransition from "../hooks/useWeekTransition";
import { useWeekParam } from "../hooks/useCalendarUrlState";
import useHorizontalSwipe from "../hooks/useHorizontalSwipe";
import useCalendarShortcuts from "../hooks/useCalendarShortcuts";
import { motionSx } from "../utils/calendarMotion";
import { useDialog } from "../contexts/DialogProvider";
import { TEACHER_PALETTE } from "./admin/OperationsBlockSchedule";

const TIME_OPTIONS = [
  "10:00", "10:30", "11:00", "11:30","12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30",
  "21:00", "21:30", "22:00"
];

const TOP_HEADER_HEIGHT = 56;   // App.js의 고정 TopHeader 높이
const WEEK_NAV_HEIGHT = 48;     // sticky 주간 이동 바 높이

// 휴일 열 텍스처 — 색맹/흑백 출력에서도 구분되도록 색 외 채널 하나를 더 쓴다.
const hatch = (c) => `repeating-linear-gradient(45deg, transparent 0 4px, ${c} 4px 5px)`;

function AdminLessonPageV2({ user, setUser, onSwitchVersion }) {
  const theme = useTheme();
  const { confirm, alert, toast } = useDialog();
  const isDark = theme.palette.mode === 'dark';
  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const isAdminOrTeacher = user.role === "ADMIN" || user.role === "TEACHER";
  const isAdmin = user.role === "ADMIN";
  const isTeacher = user.role === "TEACHER";
  const gold = theme.palette.secondary.main;

  const weekDays = ["월","화","수","목","금","토"];
  const dayKo = { SUN: "일", MON: "월", TUE: "화", WED: "수", THU: "목", FRI: "금", SAT: "토" };
  const dayNames = ["SUN","MON","TUE","WED","THU","FRI","SAT"];

  // 시간표 치수 — 솔리드 블록이라 행을 낮게 잡아도 읽힌다. 모바일 터치 타깃은 행간 포함 24px 확보.
  const CHIP_H = isWide ? 26 : 22;
  const TIME_COL = isWide ? 56 : 40;
  const GRID_COLUMNS = `${TIME_COL}px repeat(6, minmax(0, 1fr))`;
  const hatchLine = isDark ? "rgba(255,255,255,0.18)" : "rgba(17,17,17,0.16)";
  const line = `1px solid ${theme.palette.divider}`;
  // 임시 예약(결제 전 가배정)은 v1처럼 선생님 색 대신 노란 면으로 구분한다 — 빗금/텍스트 없음.
  const provBg = isDark ? "#5C5320" : "#F6C445";
  const provText = isDark ? "#F0E6B2" : "#42350A";

  // 색을 무엇에 쓸지가 역할별로 다르다.
  //  - ADMIN: 선생님이 누구인지가 관심사 → 선생님별 팔레트
  //  - TEACHER: 내 레슨이 언제인지가 관심사 → 내 레슨만 강조색, 남의 레슨은 중립
  // 내 레슨 색은 v1의 MY_LESSON_BG(파랑)를 계승하되 팔레트 톤으로 맞춘다.
  // 검정 계열은 블록(진회색)과 한 덩어리로 읽혀서 쓰지 않는다.
  const colorByTeacher = isAdmin;
  const mineBg = isDark ? "#0C447C" : "#185FA5";
  const mineText = isDark ? "#B5D4F4" : "#FFFFFF";
  const otherBg = isDark ? "#6A6862" : "#DAD7CD";
  const otherText = isDark ? "#F1EFE8" : "#44443F";

  // 솔리드 블록 언어: 예약 칸은 진한 원색 면(solid) + 밝은 글자(solidText).
  // 라이트 = bar(진한 톤) 위 흰 글자, 다크 = fill(어두운 톤) 위 밝은 글자 —
  // 운영 설정 탭 블록과 같은 조합이라 대비가 이미 검증돼 있다.
  function palOf(idx) {
    const p = TEACHER_PALETTE[idx % TEACHER_PALETTE.length];
    const m = isDark ? 1 : 0;
    return {
      fill: p.fill[m],
      bar: p.bar[m],
      text: p.text[m],
      solid: isDark ? p.fill[1] : p.bar[0],
      solidText: isDark ? p.text[1] : "#FFFFFF"
    };
  }

  function getOverrideForSlot(teacher, date) {
    return teacher.overrides?.find(o => o.date === date) || null;
  }

  function getEndTime(start) {
    if (!start) return "";
    let [h, m] = start.split(":").map(Number);
    m += 30;
    h += Math.floor(m/60);
    m = m % 60;
    if(h>23) h=23;
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
  }

  const [weekStart, setWeekStart] = useWeekParam(); // 보던 주는 URL(?week=)에 유지
  const [weekDates, setWeekDates] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [members, setMembers] = useState([]);
  const [dialog, setDialog] = useState({ open: false, reservationType: 'block-once' });
  const [unblockDialog, setUnblockDialog] = useState({ open: false });
  const [holidayDialog, setHolidayDialog] = useState({ open: false, date: null, refundTickets: true });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, date: null, holiday: null });
  const [holidays, setHolidays] = useState([]);
  const [lessonConfig, setLessonConfig] = useState(null);
  const [lessonOverrides, setLessonOverrides] = useState([]);
  const [configDialog, setConfigDialog] = useState(false);
  const [blockSchedules, setBlockSchedules] = useState([]);

  // 변동 스케줄 관리 state
  const [teacherSelectDialog, setTeacherSelectDialog] = useState(false);
  const [overrideManageDialog, setOverrideManageDialog] = useState({ open: false, teacher: null });
  const [scheduleOverrides, setScheduleOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({
    date: "",
    type: "OFF",
    work_start: "09:00",
    work_end: "18:00",
    reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [editingOverrideId, setEditingOverrideId] = useState(null);

  // 레슨 설정 불러오기
  useEffect(() => {
    api.get("/lesson-config")
      .then(res => setLessonConfig(res.data))
      .catch(() => setLessonConfig({
        weekday_start_time: "14:00",
        weekday_end_time: "22:00",
        weekend_start_time: "12:00",
        weekend_end_time: "17:00",
        slot_duration: 30
      }));
  }, []);

  // 예외 설정 불러오기
  useEffect(() => {
    api.get("/lesson-config/overrides")
      .then(res => setLessonOverrides(res.data));
  }, []);

  // 블록 스케줄 불러오기
  useEffect(() => {
    api.get("/block-schedules")
      .then(res => setBlockSchedules(res.data));
  }, []);

  // 요일별 기본 레슨 시간 (요일 컬럼 우선, 없으면 레거시 평일/주말 필드로 폴백)
  const DAY_KEYS_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const getBaseWindowForDow = (dow) => {
    if (!lessonConfig) return null;
    const key = DAY_KEYS_EN[dow];
    const s = lessonConfig[`${key}_start_time`]?.slice(0, 5);
    const e = lessonConfig[`${key}_end_time`]?.slice(0, 5);
    if (s && e) return { start: s, end: e };
    if (dow === 0) return null; // 일요일: 요일별 설정이 없으면 휴무
    const isWeekend = dow === 6;
    const ls = isWeekend ? lessonConfig.weekend_start_time?.slice(0, 5) : lessonConfig.weekday_start_time?.slice(0, 5);
    const le = isWeekend ? lessonConfig.weekend_end_time?.slice(0, 5) : lessonConfig.weekday_end_time?.slice(0, 5);
    if (ls && le) return { start: ls, end: le };
    return null;
  };

  // 날짜별 유효 설정 확인 함수
  const getEffectiveConfigForDate = (dateStr) => {
    const override = lessonOverrides.find(o => o.date === dateStr);
    const base = getBaseWindowForDow(dayjs(dateStr).day());

    if (override) {
      if (!override.is_open) {
        return { is_open: false, is_override: true, reason: override.reason };
      }
      return {
        is_open: true,
        start_time: override.start_time?.slice(0, 5) || base?.start,
        end_time: override.end_time?.slice(0, 5) || base?.end,
        is_override: true,
        reason: override.reason
      };
    }

    if (!base) return { is_open: false, is_override: false };
    return { is_open: true, start_time: base.start, end_time: base.end, is_override: false };
  };

  // 시간 구간 → 슬롯 목록
  const slotsFromRange = (start, end) => {
    const [startH, startM] = start.split(":").map(Number);
    const [endH, endM] = end.split(":").map(Number);
    const duration = lessonConfig?.slot_duration || 30;
    const slots = [];
    for (let m = startH * 60 + startM; m < endH * 60 + endM; m += duration) {
      slots.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    }
    return slots;
  };

  const getAllTimeSlots = () => {
    if (!lessonConfig) return [];

    const allSlots = [];
    for (let dow = 1; dow <= 6; dow++) {
      const base = getBaseWindowForDow(dow);
      if (base) allSlots.push(...slotsFromRange(base.start, base.end));
    }
    lessonOverrides.forEach(o => {
      if (o.is_open && o.start_time && o.end_time) {
        allSlots.push(...slotsFromRange(o.start_time.slice(0, 5), o.end_time.slice(0, 5)));
      }
    });

    return [...new Set(allSlots)].sort();
  };

  const isTimeSlotValidForDate = (dateStr, time) => {
    const effectiveConfig = getEffectiveConfigForDate(dateStr);

    if (!effectiveConfig.is_open) return false;

    const startTime = effectiveConfig.start_time;
    const endTime = effectiveConfig.end_time;

    if (!startTime || !endTime) return false;

    return time >= startTime && time < endTime;
  };

  // 블록 스케줄 확인 함수 (예외 포함)
  const getBlockScheduleInfo = (teacherId, date, time) => {
    const dayOfWeek = dayNames[dayjs(date).day()];
    const blockSchedule = blockSchedules.find(bs =>
      bs.teacher_id === teacherId &&
      bs.day_of_week === dayOfWeek &&
      bs.start_time.slice(0, 5) === time
    );

    if (!blockSchedule) return null;

    const override = blockSchedule.overrides?.find(o => o.date === date);

    return {
      blockSchedule,
      override,
      isBlocked: override ? !override.is_open : true
    };
  };

  const lessonStartTimes = getAllTimeSlots();

  const handleConfigSave = () => {
    api.get("/lesson-config")
      .then(res => setLessonConfig(res.data));
    api.get("/lesson-config/overrides")
      .then(res => setLessonOverrides(res.data));
    api.get("/block-schedules")
      .then(res => setBlockSchedules(res.data));
  };

  useEffect(()=>{
    const ws = dayjs(weekStart);
    setWeekDates(Array(6).fill().map((_,i)=>ws.add(i,"day").format("YYYY-MM-DD")));
  }, [weekStart]);

  const isHoliday = (dateStr) => holidays.some(h => h.date === dateStr);
  const getHolidayInfo = (dateStr) => holidays.find(h => h.date === dateStr);

  // 주가 바뀌면 예약과 휴일을 함께 받아 한 번에 반영하고, 그 순간 주 전환 모션을 재생한다.
  const { motion: weekMotion, loading: weekLoading } = useWeekTransition(weekStart, (ws) => {
    const endDate = dayjs(ws).add(5, "day").format("YYYY-MM-DD");
    return Promise.all([
      api.get("/lessons", { params: { week_start: ws } }).catch(() => null),
      api.get("/holidays", { params: { start_date: ws, end_date: endDate } }).catch(() => null),
    ]).then(([lessonRes, holidayRes]) => () => {
      if (lessonRes) setReservations(lessonRes.data);
      if (holidayRes) setHolidays(holidayRes.data);
    });
  });

  // 주 이동 — ◀▶ 버튼, 모바일 스와이프(시간표 위), PC 키보드(← → T) 공용
  const moveWeek = (step) => setWeekStart(dayjs(weekStart).add(7 * step, "day").format("YYYY-MM-DD"));
  const weekSwipeHandlers = useHorizontalSwipe((step) => {
    if (navigator.vibrate) navigator.vibrate(30);
    moveWeek(step);
  });
  useCalendarShortcuts({
    onPrev: () => moveWeek(-1),
    onNext: () => moveWeek(1),
    onToday: () => setWeekStart(getCurrentWeekStart()),
  });

  // 보고 있는 주의 지난 요일에도 당시 변동 스케줄이 반영되도록,
  // 주 시작일(과거)과 오늘 중 더 이른 날짜부터 변동 스케줄을 조회한다.
  function overrideFetchStart() {
    const today = dayjs().format("YYYY-MM-DD");
    return weekStart < today ? weekStart : today;
  }

  async function loadTeachersWithOverrides() {
    try {
      const res = await api.get("/teachers");
      const baseTeachers = res.data;

      const teachersWithOverrides = await Promise.all(
        baseTeachers.map(async (t) => {
          try {
            const o = await api.get(`/teachers/${t.id}/schedule-overrides`, {
              params: { start_date: overrideFetchStart() }
            });
            return { ...t, overrides: o.data };
          } catch (err) {
            return { ...t, overrides: [] };
          }
        })
      );

      setTeachers(teachersWithOverrides);
    } catch (err) {
      console.error("teacher load failed:", err);
      setTeachers([]);
    }
  }

  useEffect(()=>{
    loadTeachersWithOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  useEffect(()=>{
    if (isAdminOrTeacher) {
      api.get("/members")
        .then(res => setMembers(res.data))
        .catch(err => {
          console.error("회원 목록 불러오기 실패:", err);
          setMembers([]);
        });
    }
  }, [isAdminOrTeacher]);

  async function refreshMemberTicket(memberId){
    try{
      const res = await api.get(`/members/${memberId}`);
      setReservations(prev =>
        prev.map(r =>
          r.user?.id === memberId
            ? {...r, user: {...r.user, ticket: res.data.ticket}}
            : r
        )
      );
    }catch(err){
      console.error("refreshMemberTicket 에러:", err);
    }
  }

  function isTeacherAvailableAt(teacher, date, time) {
    const override = teacher.overrides?.find(o => o.date === date);

    if (override) {
      if (override.type === "OFF") return false;

      if (override.type === "ADJUSTED") {
        const start = override.work_start.slice(0, 5);
        const end = override.work_end.slice(0, 5);
        return time >= start && time < end;
      }
    }

    if (!teacher.schedules || teacher.schedules.length === 0) return false;

    const dayOfWeek = dayNames[dayjs(date).day()];

    return teacher.schedules.some(schedule => {
      if (schedule.day !== dayOfWeek) return false;
      const scheduleStart = schedule.start.slice(0, 5);
      const scheduleEnd = schedule.end.slice(0, 5);
      return time >= scheduleStart && time < scheduleEnd;
    });
  }

  function getReservationsForSlot(day, time){
    return reservations.filter(r => r.date===day && r.start_time.slice(0,5)===time);
  }

  function getAvailableTeachers(date, time) {
    return teachers.filter(t => isTeacherAvailableAt(t, date, time));
  }

  function getReservationCountForDate(date) {
    return reservations.filter(r => r.date === date).length;
  }

  function isBlockReservation(reservation) {
    return reservation?.user?.role === 'ADMIN' || reservation?.user?.role === 'TEACHER';
  }

  function handleCellClick(day, time, teacherId, reservation, blockInfo){
    if (blockInfo?.isBlocked && !reservation) {
      setUnblockDialog({
        open: true,
        day,
        time,
        teacherId,
        blockSchedule: blockInfo.blockSchedule,
        override: blockInfo.override,
        mode: 'date'
      });
      return;
    }

    const actionType = reservation ? "cancel" : "reserve";

    setDialog({
      open: true,
      day,
      time,
      reservation,
      action: actionType,
      teacher_id: teacherId,
      reservationType: 'block-once',
      user_id: reservation?.user?.id || null,
      adjustTicket: true,
      createPractice: true,
    });
  }

  function handleHolidayClick(date) {
    const count = getReservationCountForDate(date);
    setHolidayDialog({ open: true, date, refundTickets: true, reservationCount: count });
  }

  function handleRestoreClick(date) {
    setRestoreDialog({ open: true, date, holiday: getHolidayInfo(date) });
  }

  // 블록 해제는 두 가지 범위를 지원한다.
  //  - mode 'date'  : 해당 날짜만 예외 처리(BlockScheduleOverride 생성) — 다른 주는 계속 블록
  //  - mode 'fixed' : 고정 블록 행 자체를 삭제 — 해당 요일/시간이 모든 주에서 해제
  // 블록은 30분 단위 행으로 저장되므로(blockScheduleController.create),
  // 'fixed'도 클릭한 칸 하나(요일+시작시각)에만 적용된다.
  // 연속 구간 전체를 지우려면 칸마다 해제하거나 운영 설정 > 블록 스케줄을 쓴다.
  async function handleUnblockConfirm() {
    const { day, time, blockSchedule, mode } = unblockDialog;
    const isFixedMode = mode === 'fixed';

    try {
      if (isFixedMode) {
        // 서버가 연결된 BlockScheduleOverride도 함께 삭제한다.
        await api.delete(`/block-schedules/${blockSchedule.id}`);
        toast(`매주 ${dayKo[blockSchedule.day_of_week] || blockSchedule.day_of_week}요일 ${time} 고정 블록이 해제되었습니다.`);
      } else {
        await api.post("/block-schedules/overrides", {
          block_schedule_id: blockSchedule.id,
          date: day,
          is_open: true,
          reason: "관리자 수동 해제"
        });
        toast("해당 날짜의 블록이 해제되었습니다.");
      }

      // 블록 스케줄 다시 불러오기
      api.get("/block-schedules")
        .then(res => setBlockSchedules(res.data));

      setUnblockDialog({ open: false });
    } catch (err) {
      toast(
        err?.response?.data?.message || (isFixedMode ? "고정 블록 해제 실패" : "블록 해제 실패"),
        "error"
      );
    }
  }

  async function handleHolidayConfirm() {
    try {
      const res = await api.post("/lessons/holiday", {
        date: holidayDialog.date,
        refund_tickets: holidayDialog.refundTickets
      });

      const ticketMsg = holidayDialog.refundTickets
        ? "티켓이 반환되었습니다."
        : "티켓은 반환되지 않았습니다.";

      alert({ title: "휴일 지정 완료", message: `${res.data.message}\n삭제된 레슨: ${res.data.deletedCount}건\n${ticketMsg}` });

      setHolidayDialog({ open: false, date: null, refundTickets: true });

      api.get("/lessons", { params: { week_start: weekStart } })
        .then(res => setReservations(res.data));

      const startDate = weekDates[0];
      const endDate = weekDates[weekDates.length - 1];
      api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
        .then(res => setHolidays(res.data));
    } catch (err) {
      toast(err?.response?.data?.message || "휴일 지정 실패", "error");
    }
  }

  async function handleRestoreConfirm() {
    try {
      const res = await api.post("/lessons/restore-workday", { date: restoreDialog.date });

      let message = `${res.data.message}\n복구된 레슨: ${res.data.restoredCount}건`;
      if (res.data.ticketDeductedUsers?.length > 0) {
        message += `\n티켓 차감된 회원: ${res.data.ticketDeductedUsers.map(u => u.name).join(', ')}`;
      }

      alert({ title: "업무일 복구 완료", message });

      setRestoreDialog({ open: false, date: null, holiday: null });

      api.get("/lessons", { params: { week_start: weekStart } })
        .then(res => setReservations(res.data));

      const startDate = weekDates[0];
      const endDate = weekDates[weekDates.length - 1];
      api.get("/holidays", { params: { start_date: startDate, end_date: endDate } })
        .then(res => setHolidays(res.data));
    } catch (err) {
      toast(err?.response?.data?.message || "업무일 복구 실패", "error");
    }
  }

  async function handleDialogAction(){
    const { day, time, reservation, action, teacher_id, reservationType, user_id } = dialog;

    try {
      if(action === "reserve"){
        let targetUserId;
        let isBlock = false;

        if (reservationType === 'block-once') {
          targetUserId = user.id;
          isBlock = true;
        } else if (reservationType === 'block-fixed') {
          const dayOfWeek = dayNames[dayjs(day).day()];

          await api.post("/block-schedules", {
            teacher_id,
            day_of_week: dayOfWeek,
            start_time: time,
            end_time: getEndTime(time),
            reason: "관리자 등록"
          });

          api.get("/block-schedules")
            .then(res => setBlockSchedules(res.data));

          toast("블록 스케줄(고정)이 등록되었습니다. 매주 해당 요일/시간에 자동으로 적용됩니다.");
          setDialog({ open: false, reservationType: 'block-once' });
          return;
        } else {
          if (!user_id) {
            toast("예약할 회원을 선택해주세요.", "warning");
            return;
          }
          targetUserId = user_id;
          isBlock = false;
        }

        await api.post("/lessons", {
          date: day,
          start_time: time,
          end_time: getEndTime(time),
          teacher_id,
          user_id: targetUserId,
          is_block: isBlock,
          adjust_ticket: dialog.adjustTicket !== false,
          skip_practice: dialog.createPractice === false
        });

      } else if(action === "cancel" && reservation){
        const canceledUserId = reservation.user.id;
        const canceledUserName = reservation.user.name;
        const wasBlockReservation = isBlockReservation(reservation);

        const shouldAdjustTicket = dialog.adjustTicket !== false;
        const shouldRemovePractice = dialog.removePractice !== false;
        await api.delete(`/lessons/${reservation.id}`, {
          params: {
            adjust_ticket: shouldAdjustTicket,
            skip_practice_cleanup: !shouldRemovePractice
          }
        });

        if (!wasBlockReservation) {
          await refreshMemberTicket(canceledUserId);
          if (shouldAdjustTicket) {
            toast(`${canceledUserName}님의 레슨이 취소되어 티켓이 1개 반환되었습니다.`);
          } else {
            toast(`${canceledUserName}님의 레슨이 취소되었습니다. (티켓 변동 없음)`);
          }
        }
      }

      setDialog({ open: false, reservationType: 'block-once' });
      api.get("/lessons", { params: { week_start: weekStart } })
        .then(res => setReservations(res.data));
    } catch(err){
      console.error("handleDialogAction 에러:", err);
      toast(err?.response?.data?.message || "실패", "error");
    }
  }

  // 변동 스케줄 관련 함수 — 관리 다이얼로그에서는 지난 변동도 열람할 수 있게 전체 이력 조회
  async function fetchScheduleOverrides(teacherId) {
    try {
      const res = await api.get(`/teachers/${teacherId}/schedule-overrides`, {
        params: { include_past: true }
      });
      setScheduleOverrides(res.data);
    } catch (err) {
      console.error('변동 스케줄 조회 실패:', err);
      setScheduleOverrides([]);
    }
  }

  const overrideTodayStr = dayjs().format("YYYY-MM-DD");
  const upcomingOverrides = scheduleOverrides.filter(o => o.date >= overrideTodayStr);
  const pastOverrides = scheduleOverrides
    .filter(o => o.date < overrideTodayStr)
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  function resetOverrideForm() {
    setEditingOverrideId(null);
    setOverrideForm({ date: "", type: "OFF", work_start: "09:00", work_end: "18:00", reason: "" });
  }

  function handleEditOverrideClick(o) {
    setEditingOverrideId(o.id);
    setOverrideForm({
      date: o.date,
      type: o.type,
      work_start: o.work_start ? o.work_start.slice(0, 5) : "09:00",
      work_end: o.work_end ? o.work_end.slice(0, 5) : "18:00",
      reason: o.reason || ""
    });
  }

  async function handleSubmitOverride() {
    if (!overrideForm.date) {
      toast("날짜를 선택해주세요.", "warning");
      return;
    }

    const isEditing = !!editingOverrideId;
    setLoading(true);
    try {
      let resData;
      if (isEditing) {
        const res = await api.put(`/teachers/schedule-overrides/${editingOverrideId}`, {
          type: overrideForm.type,
          work_start: overrideForm.work_start,
          work_end: overrideForm.work_end,
          reason: overrideForm.reason
        });
        resData = res.data;
      } else {
        const res = await api.post(`/teachers/${overrideManageDialog.teacher.id}/schedule-overrides`, overrideForm);
        resData = res.data;
      }

      const migration = resData.migration;
      let alertMsg = "✅ " + resData.message;
      if (migration && migration.migrated > 0) {
        const tc = migration.activeTeacherCount;
        const modeLabel = tc <= 1 ? '같은 방(레슨+연습)' : tc >= 3 ? '분리(레슨 1-3, 연습 4-6)' : '분리(레슨 1-2, 연습 3-6)';
        alertMsg += `\n\n📋 연습실 재배정: ${migration.migrated}건 → ${modeLabel} 모드 (선생님 ${tc}명)`;
        if (migration.manualMoved > 0) {
          alertMsg += `\n🔄 수동 연습실 예약 ${migration.manualMoved}건이 빈 연습실로 이동되었습니다.`;
        }
        if (migration.manualCancelled > 0) {
          alertMsg += `\n⚠️ 수동 연습실 예약 ${migration.manualCancelled}건이 빈 방 없어 취소되었습니다.`;
        }
      }
      alert({ title: isEditing ? "변동 스케줄 수정 완료" : "변동 스케줄 등록 완료", message: alertMsg, severity: "success" });
      resetOverrideForm();
      fetchScheduleOverrides(overrideManageDialog.teacher.id);

      await loadTeachersWithOverrides();
    } catch (e) {
      const errorMessage = e?.response?.data?.message || (isEditing ? '변동 스케줄 수정 실패' : '변동 스케줄 등록 실패');
      alert({ title: `${isEditing ? '수정' : '등록'} 실패`, message: errorMessage, severity: "error" });
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteOverride(overrideId) {
    if (!(await confirm({ title: "변동 스케줄 삭제", message: "정말 이 변동 스케줄을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;

    try {
      const deleteRes = await api.delete(`/teachers/schedule-overrides/${overrideId}`);
      const delMigration = deleteRes.data.migration;
      let delAlertMsg = "✅ " + deleteRes.data.message;
      if (delMigration && delMigration.migrated > 0) {
        const tc = delMigration.activeTeacherCount;
        const modeLabel = tc <= 1 ? '같은 방(레슨+연습)' : tc >= 3 ? '분리(레슨 1-3, 연습 4-6)' : '분리(레슨 1-2, 연습 3-6)';
        delAlertMsg += `\n\n📋 연습실 재배정: ${delMigration.migrated}건 → ${modeLabel} 모드 (선생님 ${tc}명)`;
        if (delMigration.manualMoved > 0) {
          delAlertMsg += `\n🔄 수동 연습실 예약 ${delMigration.manualMoved}건이 빈 연습실로 이동되었습니다.`;
        }
        if (delMigration.manualCancelled > 0) {
          delAlertMsg += `\n⚠️ 수동 연습실 예약 ${delMigration.manualCancelled}건이 빈 방 없어 취소되었습니다.`;
        }
      }
      alert({ title: "변동 스케줄 삭제 완료", message: delAlertMsg, severity: "success" });
      fetchScheduleOverrides(overrideManageDialog.teacher.id);

      await loadTeachersWithOverrides();
    } catch (e) {
      alert({ title: "삭제 실패", message: "변동 스케줄에 예약된 사용자가 있습니다.", severity: "error" });
    }
  }

  function openOverrideManage(teacher) {
    fetchScheduleOverrides(teacher.id);
    setOverrideManageDialog({ open: true, teacher });
    resetOverrideForm();
  }

  if (!lessonConfig) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }

  // ── 셀 안의 슬롯 행 ────────────────────────────────────────────────
  // 솔리드 블록 언어 (구글 캘린더식): 예약 = 진한 원색 면 + 밝은 글자,
  // 블록 = 진회색 면, 빈자리 = 점선 테두리 + 선생님 이름.
  // 컬러 바/이니셜 배지/둥근 칩은 쓰지 않는다 — 한 행에는 이름 하나만.
  function renderSlotChip(day, time, teacher, teacherIdx) {
    const reservation = getReservationsForSlot(day, time).find(r => r.teacher_id === teacher.id);
    const blockInfo = getBlockScheduleInfo(teacher.id, day, time);
    const isFixedBlock = !!blockInfo?.isBlocked && !reservation;
    const isOnceBlock = isBlockReservation(reservation);
    const override = getOverrideForSlot(teacher, day);
    const isProvisional = !!reservation?.is_provisional;
    const isMine = isTeacher && Number(teacher.id) === Number(user.id);
    const p = palOf(teacherIdx);

    const base = {
      minHeight: CHIP_H,
      borderRadius: "3px",
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      cursor: "pointer",
      px: isWide ? 1 : 0.5,
      fontSize: isWide ? 13 : 11.5,
      lineHeight: 1.2,
      transition: "filter 0.12s ease, border-color 0.12s ease, color 0.12s ease",
      "&:hover": { filter: isDark ? "brightness(1.18)" : "brightness(0.92)" }
    };

    const onClick = () => handleCellClick(day, time, teacher.id, reservation, blockInfo);

    // 블록 — 무채색 진회색 면. 고정/일회성은 표시상 구분하지 않는다(동작은 클릭 다이얼로그에서 갈라짐).
    if (isFixedBlock || isOnceBlock) {
      return (
        <Box
          key={teacher.id}
          onClick={onClick}
          title={`${teacher.name} 선생님 · 블록`}
          sx={{
            ...base,
            bgcolor: isDark ? "#3E3E3E" : "#5F5C54",
            color: isDark ? "#BDBDBD" : "#FAFAF7"
          }}
        >
          <span style={{ whiteSpace: "nowrap" }}>블록</span>
        </Box>
      );
    }

    // 예약 — 임시 예약은 선생님 색 대신 노란 면(v1 문법). PC는 회원·선생님 이름을 함께 표기.
    if (reservation) {
      // 선생님 계정은 임시/확정을 구분하지 않는다(결제 여부는 관리자 업무). 내 레슨 / 남의 레슨 둘뿐.
      let slotBg;
      let slotText;
      if (!colorByTeacher) {
        slotBg = isMine ? mineBg : otherBg;
        slotText = isMine ? mineText : otherText;
      } else if (isProvisional) {
        slotBg = provBg;
        slotText = provText;
      } else {
        slotBg = p.solid;
        slotText = p.solidText;
      }

      return (
        <Box
          key={teacher.id}
          onClick={onClick}
          title={`${reservation.user.name} · ${teacher.name} 선생님${isProvisional && colorByTeacher ? " (임시 예약)" : ""}`}
          sx={{
            ...base,
            bgcolor: slotBg,
            color: slotText,
            fontWeight: 500,
            gap: 0.75
          }}
        >
          <Box component="span" sx={{
            minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
          }}>
            {reservation.user.name}
          </Box>
          {isWide && (
            <Box component="span" sx={{ flex: "none", fontSize: 11.5, fontWeight: 400, opacity: 0.8, whiteSpace: "nowrap" }}>
              · {teacher.name}
            </Box>
          )}
        </Box>
      );
    }

    // 빈자리 — 점선 테두리 + 선생님 이름(성 포함, PC는 "선생님" 호칭까지).
    const isAdjusted = override?.type === "ADJUSTED";
    const emptyAccent = colorByTeacher ? p.solid : (isMine ? mineBg : otherText);
    return (
      <Box
        key={teacher.id}
        onClick={onClick}
        title={`${teacher.name} 선생님 · 빈자리${isAdjusted ? " (근무시간 조정일)" : ""}`}
        sx={{
          ...base,
          border: "1px dashed",
          borderColor: isAdjusted ? emptyAccent : (isDark ? "#555" : "#C9C6BC"),
          color: isAdjusted ? emptyAccent : "text.disabled",
          fontSize: isWide ? 12 : 11,
          "&:hover": {
            borderColor: emptyAccent,
            color: emptyAccent,
            bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(17,17,17,0.03)"
          }
        }}
      >
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {isWide ? `${teacher.name} 선생님` : teacher.name}
        </span>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2, pb: 2, maxWidth: 900, mx: "auto", px: { xs: 0, md: 2 } }}>
      {/* 주간 이동 — TopHeader 바로 아래에 고정 */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          top: `${TOP_HEADER_HEIGHT}px`,
          zIndex: 3,
          height: WEEK_NAV_HEIGHT,
          bgcolor: "background.default",
          borderBottom: 1,
          borderColor: "divider",
          mb: 1
        }}
      >
        <Button
          onClick={() => moveWeek(-1)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          ◀
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <WeekJumpPicker
            weekStart={weekStart}
            onChange={setWeekStart}
            label={weekDates.length && `${weekDates[0]} ~ ${weekDates.at(-1)}`}
            labelSx={{ fontWeight: 500, fontSize: "clamp(0.85rem, 3.8vw, 1rem)" }}
          />
          {isAdmin && (
            <IconButton onClick={() => setConfigDialog(true)} color="primary" size="small">
              <Settings />
            </IconButton>
          )}
        </Box>

        <Button
          onClick={() => moveWeek(1)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          ▶
        </Button>

        {/* 주 데이터 로딩이 길어질 때만 이동 바 하단에 얇은 진행 막대 */}
        {weekLoading && (
          <LinearProgress sx={{ position: "absolute", left: 0, right: 0, bottom: -1, height: 2 }} />
        )}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end", px: { xs: 1, md: 0 }, mb: 1 }}>
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={onSwitchVersion}
          sx={{ color: "text.secondary" }}
        >
          이전 버전으로
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          // overflowX는 "auto"가 아니라 "clip" — auto면 overflowY까지 auto로 승격되어
          // Paper가 스크롤 컨테이너가 되고 내부 요일 헤더의 sticky가 동작하지 않는다.
          overflowX: "clip",
          borderRadius: { xs: 0, md: "12px" },
          // 네 변 모두 색까지 명시한다. borderLeft: 1 처럼 색 없는 축약을 반응형 값으로 쓰면
          // MUI가 미디어쿼리 블록을 뒤로 병합해서 borderColor보다 나중에 적용되고,
          // 색이 currentColor(잉크 블랙)로 떨어져 좌우만 검은 선이 된다.
          borderTop: line,
          borderBottom: line,
          borderLeft: { xs: "none", md: line },
          borderRight: { xs: "none", md: line },
          // 주 전환 모션 — 요일 헤더와 각 시간 행(직계 자식)에 같이 건다.
          // 감싸는 Box 없이 직계 자식에 걸어 표 구조(sticky 요일 헤더 포함)를 그대로 둔다.
          ...motionSx(weekMotion, "& > *")
        }}
        {...weekSwipeHandlers}
      >
        {/* 요일 헤더 — 주간 이동 바 바로 아래에 고정 */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: GRID_COLUMNS,
          position: "sticky",
          top: `${TOP_HEADER_HEIGHT + WEEK_NAV_HEIGHT}px`,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          boxShadow: isDark ? "0 2px 4px rgba(0,0,0,0.45)" : "0 2px 4px rgba(17,17,17,0.08)"
        }}>
          <Box />
          {weekDates.map((d, i) => {
            const effectiveConfig = getEffectiveConfigForDate(d);
            const holiday = isHoliday(d);
            const isToday = d === dayjs().format("YYYY-MM-DD");

            return (
              <Box key={d} sx={{
                borderLeft: 1,
                borderColor: "divider",
                borderTop: isToday ? `2px solid ${gold}` : "2px solid transparent",
                px: 0.5, pt: 0.75, pb: 0.5,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25,
                bgcolor: holiday ? (isDark ? "rgba(255,255,255,0.03)" : "rgba(17,17,17,0.03)") : "transparent"
              }}>
                <Box sx={{
                  display: "flex", alignItems: "baseline", gap: 0.5,
                  color: holiday ? "text.disabled" : "text.primary"
                }}>
                  <Box component="span" sx={{ fontSize: isWide ? 14 : 12, fontWeight: 500 }}>{weekDays[i]}</Box>
                  <Box component="span" sx={{ fontSize: isWide ? 12 : 11, color: "text.secondary" }}>
                    {d.slice(5).replace("-", "/")}
                  </Box>
                </Box>

                {/* 모바일은 휴일 텍스트 생략 — 달력 아이콘(상태 색)과 열 전체 빗금이 대신한다. */}
                {holiday ? (isWide && (
                  <Chip label="휴일" size="small" color="error" variant="outlined"
                    sx={{ height: 17, fontSize: 11, "& .MuiChip-label": { px: 0.6 } }} />
                )) : effectiveConfig.is_override && (
                  <Chip label={effectiveConfig.is_open ? "시간변경" : "휴무"} size="small"
                    color={effectiveConfig.is_open ? "warning" : "error"}
                    sx={{ height: 17, fontSize: 11, maxWidth: "100%", "& .MuiChip-label": { px: 0.6 } }} />
                )}

                {isAdmin && (
                  <Tooltip title={holiday ? "휴일 지정 취소" : "휴일 지정"}>
                    <IconButton
                      size="small"
                      color={holiday ? "primary" : "default"}
                      onClick={() => (holiday ? handleRestoreClick(d) : handleHolidayClick(d))}
                      sx={{ p: 0.25 }}
                    >
                      {holiday
                        ? <EventAvailable sx={{ fontSize: 16 }} />
                        : <EventBusy sx={{ fontSize: 16, color: "text.disabled" }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>

        {lessonStartTimes.map(time => {
          const isValidForAnyDay = weekDates.some(day => isTimeSlotValidForDate(day, time));
          if (!isValidForAnyDay) return null;

          // 정시는 진한 선, 30분은 hairline — 엑셀식 굵은 격자 대신 리듬만 준다.
          const isHourStart = time.endsWith(":00");
          const rowBorder = isHourStart
            ? (isDark ? "rgba(255,255,255,0.22)" : "rgba(17,17,17,0.18)")
            : theme.palette.divider;

          return (
            <Box key={time} sx={{
              display: "grid",
              gridTemplateColumns: GRID_COLUMNS,
              borderTop: `1px solid ${rowBorder}`
            }}>
              <Box sx={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                py: 0.5,
                color: isHourStart ? "text.primary" : "text.secondary",
                fontSize: isWide ? 13 : 11,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.2
              }}>
                <Box component="span" sx={{ fontWeight: isHourStart ? 500 : 400 }}>{time}</Box>
                {isWide && (
                  <Box component="span" sx={{ fontSize: 11, color: "text.disabled" }}>~{getEndTime(time)}</Box>
                )}
              </Box>

              {weekDates.map(day => {
                // 여백 최소화 — 블록이 셀을 거의 가득 채워 표 자체가 색 면으로 읽히게 한다.
                const cellSx = {
                  borderLeft: 1,
                  borderColor: "divider",
                  p: "2px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  justifyContent: "center",
                  minHeight: CHIP_H + 6
                };

                if (!isTimeSlotValidForDate(day, time)) {
                  return <Box key={day} sx={{ ...cellSx, bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(17,17,17,0.02)" }} />;
                }

                // 휴일 열은 헤더에 배지가 고정돼 있으므로 셀마다 "휴일"을 반복하지 않는다.
                if (isHoliday(day)) {
                  return (
                    <Box key={day} sx={{
                      ...cellSx,
                      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(17,17,17,0.03)",
                      backgroundImage: hatch(hatchLine)
                    }} />
                  );
                }

                const availableTeachers = getAvailableTeachers(day, time);

                if (availableTeachers.length === 0) {
                  return (
                    <Box key={day} sx={{ ...cellSx, alignItems: "center", justifyContent: "center" }}>
                      <Typography sx={{ fontSize: isWide ? 12 : 11, color: "text.disabled", whiteSpace: "nowrap" }}>
                        미출근
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <Box key={day} sx={cellSx}>
                    {availableTeachers.map(teacher =>
                      renderSlotChip(day, time, teacher, teachers.findIndex(t => t.id === teacher.id))
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Paper>

      {/* 범례 — 색이 무엇을 뜻하는지 표에서 바로 확인 */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5,
        mt: 1.5, px: { xs: 1.25, md: 0 }, fontSize: 12, color: "text.secondary"
      }}>
        {colorByTeacher ? (
          teachers.map((t, idx) => (
            <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: palOf(idx).solid }} />
              <span>{t.name}</span>
            </Box>
          ))
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: mineBg }} />
              <span>내 레슨</span>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: otherBg }} />
              <span>다른 선생님</span>
            </Box>
          </>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: "3px", border: "1px dashed", borderColor: isDark ? "#555" : "#C9C6BC" }} />
          <span>빈자리</span>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: isDark ? "#3E3E3E" : "#5F5C54" }} />
          <span>블록</span>
        </Box>
        {colorByTeacher && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: provBg }} />
            <span>임시 예약</span>
          </Box>
        )}
      </Box>

      {/* 선생님 근무 시간 */}
      <Box sx={{ mt: 2, px: { xs: 1.25, md: 0 } }}>
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider"
        }}>
          <Typography variant="body2" fontWeight={500}>선생님 근무 시간</Typography>
          {isAdmin && (
            <IconButton size="small" color="primary" onClick={() => setTeacherSelectDialog(true)}>
              <Settings />
            </IconButton>
          )}
        </Box>
        <Stack direction="column" gap={1.5} divider={<Divider />}>
          {teachers.map((teacher, idx) => {
            const isSelf = isTeacher && Number(teacher.id) === Number(user.id);
            const swatch = colorByTeacher ? palOf(idx).solid : (isSelf ? mineBg : otherBg);
            return (
              <Box key={teacher.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: swatch }} />
                  <Typography variant="body2" fontWeight={500}>{teacher.name} 선생님</Typography>
                </Box>

                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(auto-fill, minmax(150px, 1fr))" },
                  gap: 0.75
                }}>
                  {teacher.schedules?.map((s, i) => (
                    <Box key={`s-${i}`} sx={{
                      border: 1, borderColor: "divider", borderRadius: "8px",
                      px: 1, py: 0.5,
                      display: "flex", alignItems: "baseline", gap: 0.75,
                      fontSize: "0.75rem", whiteSpace: "nowrap"
                    }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>{dayKo[s.day] || s.day}</Box>
                      <Box component="span" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                        {s.start.slice(0,5)}-{s.end.slice(0,5)}
                      </Box>
                    </Box>
                  ))}
                  {teacher.overrides?.map((o, i) => (
                    <Box key={`o-${i}`} sx={{
                      border: 1,
                      borderColor: o.type === "OFF" ? "error.main" : "info.main",
                      color: o.type === "OFF" ? "error.main" : "info.main",
                      borderRadius: "8px",
                      px: 1, py: 0.5,
                      display: "flex", alignItems: "baseline", gap: 0.75,
                      fontSize: "0.75rem", whiteSpace: "nowrap",
                      opacity: o.date < overrideTodayStr ? 0.5 : 1
                    }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {dayjs(o.date).format("M/D")} {dayKo[dayNames[dayjs(o.date).day()]]}
                      </Box>
                      <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {o.type === "OFF" ? "휴무" : `${o.work_start.slice(0,5)}-${o.work_end.slice(0,5)}`}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* 예약/취소 다이얼로그 */}
      <Dialog open={dialog.open} onClose={()=>setDialog({ open: false, reservationType: 'block-once' })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box component="span" sx={{ display: "block", fontWeight: 700 }}>
            {dialog.action === "reserve"
              ? "레슨 예약"
              : isBlockReservation(dialog.reservation)
                ? "블록 예약 취소"
                : "레슨 예약 취소"
            }
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: "0.85rem", fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
            {dialog.day && `${dayjs(dialog.day).format("YYYY년 MM월 DD일")} (${dayKo[dayNames[dayjs(dialog.day).day()]]})`}
            {teachers.find(t=>t.id===dialog.teacher_id)?.name && ` · ${teachers.find(t=>t.id===dialog.teacher_id)?.name} 선생님`}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, px: 2, py: 1.25, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAF7" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
              <Typography variant="body2" color="text.secondary">시간</Typography>
              <Typography variant="body2" fontWeight={600}>{dialog.time} ~ {getEndTime(dialog.time)}</Typography>
            </Box>
            {dialog.action === "cancel" && !isBlockReservation(dialog.reservation) && dialog.reservation?.user?.name && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                <Typography variant="body2" color="text.secondary">예약자</Typography>
                <Typography variant="body2" fontWeight={600}>{dialog.reservation.user.name}</Typography>
              </Box>
            )}
          </Box>

          {dialog.action === "reserve" && (
            <>
              <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>예약 유형</Typography>
              {[
                { value: 'block-once', title: '블록 예약 (일회성)', desc: '해당 날짜/시간만 차단합니다' },
                { value: 'block-fixed', title: '블록 예약 (고정)', desc: '매주 해당 요일/시간을 자동으로 차단합니다' },
                { value: 'member', title: '회원 대리 예약', desc: '선택한 회원의 레슨을 대신 예약합니다' },
              ].map(opt => {
                const selected = dialog.reservationType === opt.value;
                return (
                  <ButtonBase
                    key={opt.value}
                    onClick={() => setDialog(d => ({ ...d, reservationType: opt.value, user_id: null }))}
                    sx={{
                      display: "flex", width: "100%", textAlign: "left", alignItems: "center", gap: 1.25,
                      border: "1px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      borderRadius: 2.5, px: 1.75, py: 1.1, mb: 1,
                      bgcolor: selected ? (isDark ? "rgba(255,255,255,0.06)" : "#FAFAF7") : "transparent",
                      transition: "border-color 0.15s ease, background-color 0.15s ease",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{opt.title}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">{opt.desc}</Typography>
                    </Box>
                    {selected && <Check sx={{ fontSize: 18, color: "secondary.main" }} />}
                  </ButtonBase>
                );
              })}

              {dialog.reservationType === 'member' && (
                <>
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>예약할 회원</Typography>
                  <MemberPicker
                    members={members.filter(m => m.is_active !== false)}
                    selectedId={dialog.user_id}
                    onSelect={(id) => setDialog(d => ({ ...d, user_id: id }))}
                    maxHeight={190}
                  />
                  <Box sx={{ mt: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dialog.adjustTicket !== false}
                          onChange={(e) => setDialog(d => ({ ...d, adjustTicket: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">티켓 1개 차감</Typography>}
                      sx={{ display: "flex", mt: 0.5 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dialog.createPractice !== false}
                          onChange={(e) => setDialog(d => ({ ...d, createPractice: e.target.checked }))}
                        />
                      }
                      label={<Typography variant="body2">연습실 자동 생성</Typography>}
                      sx={{ display: "flex", mt: -0.5 }}
                    />
                  </Box>
                </>
              )}
            </>
          )}

          {dialog.action === "cancel" && !isBlockReservation(dialog.reservation) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error">
                {dialog.reservation?.user?.name}님의 레슨을 취소하시겠습니까?
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dialog.adjustTicket !== false}
                    onChange={(e) => setDialog(d => ({ ...d, adjustTicket: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">티켓 1개 반환</Typography>}
                sx={{ display: "flex", mt: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dialog.removePractice !== false}
                    onChange={(e) => setDialog(d => ({ ...d, removePractice: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">연습실 데이터 함께 제거</Typography>}
                sx={{ display: "flex", mt: -0.5 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={()=>setDialog({ open: false, reservationType: 'block-once' })} variant="outlined" color="inherit" sx={{ flex: 1 }}>
            {dialog.action === "reserve" ? "취소" : "닫기"}
          </Button>
          <Button
            onClick={handleDialogAction}
            color={dialog.action==="cancel"?"error":"primary"}
            variant="contained"
            disabled={dialog.action === "reserve" && dialog.reservationType === 'member' && !dialog.user_id}
            sx={{ flex: 2 }}
          >
            {dialog.action === "reserve"
              ? (dialog.reservationType === 'block-once' ? "블록 예약" :
                 dialog.reservationType === 'block-fixed' ? "블록 스케줄 등록" :
                 dialog.user_id
                   ? `${members.find(m => m.id === dialog.user_id)?.name || "회원"} 대리 예약`
                   : "회원을 선택하세요")
              : "예약 취소"
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* 블록 해제 다이얼로그 — 해제 범위(이 날짜만 / 고정 전체)를 고른다 */}
      <Dialog open={unblockDialog.open} onClose={() => setUnblockDialog({ open: false })} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 700 }}>
            <LockOpen color="primary" fontSize="small" />
            블록 해제
          </Box>
          <Box component="span" sx={{ display: "block", fontSize: "0.85rem", fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
            {unblockDialog.day && `${dayjs(unblockDialog.day).format("YYYY년 MM월 DD일")} (${dayKo[dayNames[dayjs(unblockDialog.day).day()]]})`}
            {teachers.find(t => t.id === unblockDialog.teacherId)?.name && ` · ${teachers.find(t => t.id === unblockDialog.teacherId)?.name} 선생님`}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2.5, px: 2, py: 1.25, bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAF7" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
              <Typography variant="body2" color="text.secondary">시간</Typography>
              <Typography variant="body2" fontWeight={600}>{unblockDialog.time} ~ {getEndTime(unblockDialog.time)}</Typography>
            </Box>
            {unblockDialog.blockSchedule?.reason && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                <Typography variant="body2" color="text.secondary">블록 사유</Typography>
                <Typography variant="body2" fontWeight={600}>{unblockDialog.blockSchedule.reason}</Typography>
              </Box>
            )}
          </Box>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>해제 범위</Typography>
          {[
            {
              value: 'date',
              title: '이 날짜만 해제',
              desc: unblockDialog.day
                ? `${dayjs(unblockDialog.day).format("M/D")}만 예약 가능, 다른 주는 계속 블록`
                : '해당 날짜만 예약 가능하게 합니다'
            },
            {
              value: 'fixed',
              title: '고정 블록 해제',
              desc: unblockDialog.day
                ? `매주 ${dayKo[dayNames[dayjs(unblockDialog.day).day()]]}요일 ${unblockDialog.time} 블록을 삭제`
                : '해당 요일/시간의 고정 블록을 삭제합니다'
            },
          ].map(opt => {
            const isFixedOpt = opt.value === 'fixed';
            const selected = (unblockDialog.mode || 'date') === opt.value;
            return (
              <ButtonBase
                key={opt.value}
                onClick={() => setUnblockDialog(d => ({ ...d, mode: opt.value }))}
                sx={{
                  display: "flex", width: "100%", textAlign: "left", alignItems: "center", gap: 1.25,
                  border: "1px solid",
                  borderColor: selected ? (isFixedOpt ? "error.main" : "primary.main") : "divider",
                  borderRadius: 2.5, px: 1.75, py: 1.1, mb: 1,
                  bgcolor: selected ? (isDark ? "rgba(255,255,255,0.06)" : "#FAFAF7") : "transparent",
                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{opt.title}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{opt.desc}</Typography>
                </Box>
                {selected && <Check sx={{ fontSize: 18, color: isFixedOpt ? "error.main" : "secondary.main" }} />}
              </ButtonBase>
            );
          })}

          {unblockDialog.mode === 'fixed' && (
            <Alert severity="warning" sx={{ mt: 0.5, py: 0.25, alignItems: "center" }}>
              <Typography variant="caption" display="block">
                지난 주를 포함한 모든 주에 적용되며, 이 시간대에 등록된 날짜별 예외 설정도 함께 삭제됩니다.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={() => setUnblockDialog({ open: false })} variant="outlined" color="inherit" sx={{ flex: 1 }}>
            취소
          </Button>
          <Button
            onClick={handleUnblockConfirm}
            color={unblockDialog.mode === 'fixed' ? "error" : "primary"}
            variant="contained"
            sx={{ flex: 2 }}
          >
            {unblockDialog.mode === 'fixed' ? "고정 블록 해제" : "이 날짜만 해제"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 휴일 지정 다이얼로그 */}
      <Dialog open={holidayDialog.open} onClose={() => setHolidayDialog({ open: false, date: null, refundTickets: true })}>
        <DialogTitle>휴일 지정</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{holidayDialog.date}</strong>을(를) 휴일로 지정하시겠습니까?
          </Typography>
          <Typography color="error" sx={{ mb: 2 }}>
            해당 일에 예약된 레슨 {holidayDialog.reservationCount}건이 모두 삭제됩니다.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={holidayDialog.refundTickets}
                onChange={(e) => setHolidayDialog(prev => ({ ...prev, refundTickets: e.target.checked }))}
              />
            }
            label="회원에게 레슨 티켓 반환"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {holidayDialog.refundTickets
              ? "삭제되는 레슨의 회원에게 티켓이 1개씩 반환됩니다."
              : "티켓이 반환되지 않습니다."}
          </Typography>
          <DateImpactPreview date={holidayDialog.date} change={{ kind: "HOLIDAY" }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialog({ open: false, date: null, refundTickets: true })}>취소</Button>
          <Button onClick={handleHolidayConfirm} color="error" variant="contained">휴일 지정</Button>
        </DialogActions>
      </Dialog>

      {/* 업무일 복구 다이얼로그 */}
      <Dialog open={restoreDialog.open} onClose={() => setRestoreDialog({ open: false, date: null, holiday: null })}>
        <DialogTitle>휴일 지정 취소</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{restoreDialog.date}</strong> 휴일 지정을 취소하시겠습니까?
          </Typography>

          {restoreDialog.holiday?.backup_lessons?.length > 0 ? (
            <>
              <Typography sx={{ mb: 1 }}>
                복구될 레슨: <strong>{restoreDialog.holiday.backup_lessons.length}건</strong>
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 2 }}>
                {restoreDialog.holiday.backup_lessons.map((lesson, idx) => (
                  <Typography key={idx} variant="body2">
                    • {lesson.user_name} - {lesson.start_time?.slice(0,5)}
                  </Typography>
                ))}
              </Box>
              {restoreDialog.holiday.refund_tickets && (
                <Typography color="warning.main" variant="body2">
                  ⚠️ 휴일 지정 시 티켓이 반환되었으므로, 복구 시 해당 회원들의 티켓이 다시 차감됩니다.
                </Typography>
              )}
            </>
          ) : (
            <Typography color="text.secondary">복구할 레슨이 없습니다.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, date: null, holiday: null })}>취소</Button>
          <Button onClick={handleRestoreConfirm} color="primary" variant="contained">휴일 지정 취소</Button>
        </DialogActions>
      </Dialog>

      {/* 선생님 선택 다이얼로그 */}
      <Dialog open={teacherSelectDialog} onClose={() => setTeacherSelectDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday />
            변동 스케줄 관리
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            변동 스케줄을 관리할 선생님을 선택하세요.
          </Typography>
          <Stack spacing={1}>
            {teachers.map(teacher => (
              <Button
                key={teacher.id}
                variant="outlined"
                fullWidth
                onClick={() => {
                  setTeacherSelectDialog(false);
                  openOverrideManage(teacher);
                }}
                sx={{ justifyContent: "flex-start", textAlign: "left" }}
              >
                {teacher.name} 선생님
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeacherSelectDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 변동 스케줄 관리 다이얼로그 */}
      <Dialog
        open={overrideManageDialog.open}
        onClose={() => { setOverrideManageDialog({ open: false, teacher: null }); resetOverrideForm(); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>변동 스케줄 관리 - {overrideManageDialog.teacher?.name} 선생님</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="info">
              선생님의 휴가 또는 근무 시간 변경시 변동되는 스케줄을 추가합니다.
            </Alert>

            <Box sx={{ p: 2, bgcolor: isDark ? "grey.800" : "#f5f5f5", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                {editingOverrideId ? "변동 스케줄 수정" : "새 변동 스케줄 등록"}
              </Typography>

              <Stack spacing={2}>
                <DateField
                  label="날짜"
                  value={overrideForm.date}
                  onChange={date => setOverrideForm({ ...overrideForm, date })}
                  fullWidth
                  disabled={!!editingOverrideId}
                  helperText={editingOverrideId ? "날짜는 수정할 수 없습니다. 변경하려면 삭제 후 다시 등록하세요." : undefined}
                  disablePast
                />

                <FormControl fullWidth>
                  <InputLabel>변동 유형</InputLabel>
                  <Select
                    label="변동 유형"
                    value={overrideForm.type}
                    onChange={e => setOverrideForm({ ...overrideForm, type: e.target.value })}
                  >
                    <MenuItem value="OFF">휴가</MenuItem>
                    <MenuItem value="ADJUSTED">근무 시간 조정</MenuItem>
                  </Select>
                </FormControl>

                {overrideForm.type === "ADJUSTED" && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>시작 시간</InputLabel>
                      <Select
                        label="시작 시간"
                        value={overrideForm.work_start}
                        onChange={e => setOverrideForm({ ...overrideForm, work_start: e.target.value })}
                      >
                        {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>종료 시간</InputLabel>
                      <Select
                        label="종료 시간"
                        value={overrideForm.work_end}
                        onChange={e => setOverrideForm({ ...overrideForm, work_end: e.target.value })}
                      >
                        {TIME_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </>
                )}

                <TextField
                  label="변동 사유 (선택)"
                  value={overrideForm.reason}
                  onChange={e => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />

                {overrideForm.date && (
                  <DateImpactPreview
                    date={overrideForm.date}
                    change={{
                      kind: "TEACHER_OVERRIDE",
                      payload: {
                        teacher_id: overrideManageDialog.teacher?.id,
                        type: overrideForm.type,
                        work_start: overrideForm.work_start,
                        work_end: overrideForm.work_end
                      }
                    }}
                  />
                )}

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={handleSubmitOverride} disabled={loading} fullWidth>
                    {editingOverrideId ? "수정 저장" : "등록"}
                  </Button>
                  {editingOverrideId && (
                    <Button variant="outlined" onClick={resetOverrideForm} disabled={loading}>취소</Button>
                  )}
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>등록된 변동 스케줄</Typography>

              {scheduleOverrides.length === 0 ? (
                <Typography variant="body2" color="text.secondary">등록된 변동 스케줄이 없습니다.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>날짜</TableCell>
                        <TableCell>유형</TableCell>
                        <TableCell>시간</TableCell>
                        <TableCell>사유</TableCell>
                        <TableCell align="center">관리</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcomingOverrides.map(override => (
                        <TableRow key={override.id}>
                          <TableCell>{override.date}</TableCell>
                          <TableCell>
                            <Chip
                              label={override.type === "OFF" ? "휴가" : "시간 조정"}
                              size="small"
                              color={override.type === "OFF" ? "error" : "warning"}
                            />
                          </TableCell>
                          <TableCell>
                            {override.type === "OFF"
                              ? "출근 안 함"
                              : `${override.work_start?.slice(0, 5)} - ${override.work_end?.slice(0, 5)}`
                            }
                          </TableCell>
                          <TableCell>{override.reason || "-"}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="primary" onClick={() => handleEditOverrideClick(override)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteOverride(override.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {pastOverrides.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0.5, bgcolor: isDark ? "grey.900" : "grey.50" }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              지난 변동 (열람 전용)
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {pastOverrides.map(override => (
                        <TableRow key={override.id} sx={{ opacity: 0.55 }}>
                          <TableCell>{override.date}</TableCell>
                          <TableCell>
                            <Chip
                              label={override.type === "OFF" ? "휴가" : "시간 조정"}
                              size="small"
                              color={override.type === "OFF" ? "error" : "warning"}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {override.type === "OFF"
                              ? "출근 안 함"
                              : `${override.work_start?.slice(0, 5)} - ${override.work_end?.slice(0, 5)}`
                            }
                          </TableCell>
                          <TableCell>{override.reason || "-"}</TableCell>
                          <TableCell align="center">-</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideManageDialog({ open: false, teacher: null }); resetOverrideForm(); }}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 레슨 설정 다이얼로그 */}
      <LessonConfigDialog
        open={configDialog}
        onClose={() => setConfigDialog(false)}
        onSave={handleConfigSave}
      />
    </Box>
  );
}

export default AdminLessonPageV2;
